import { FC } from "react"

import { useTranslation } from "react-i18next"

import Chevron from "../../assets/icons/chevron-icon.svg"
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg"

import { StatOption } from "./statsConfig"

interface Props {
  dropdownOpen: boolean
  setDropdownOpen: (v: boolean) => void

  selectedStats: string[]
  options: StatOption[]

  onToggleStat: (value: string, checked: boolean) => void
  onRemoveStat: (value: string) => void
}

export const StatsSelector: FC<Props> = ({ dropdownOpen, setDropdownOpen, selectedStats, options, onToggleStat, onRemoveStat }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div className="relative w-full max-w-lg">
      <div
        className="flex justify-between items-center rounded-full p-[3px] cursor-pointer border border-zinc-300 dark:border-[rgba(255,255,255,0.1)]"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <div className="flex flex-wrap gap-2">
          {selectedStats.length === 0 && <span className="text-gray-500 ml-4">{t("charts.selectStats")}</span>}

          {selectedStats.map((value) => {
            const stat = options.find((opt) => opt.value === value)
            const label = stat?.label ? t(stat.label) : value

            return (
              <div
                key={value}
                className="text-sm px-2 py-1 rounded-full flex items-center gap-2 transition-all hover:opacity-60 border border-[rgba(0,0,0,0.1)] text-black  dark:border dark:border-[rgba(255,255,255,0.1)] dark:text-white dark:bg-[linear-gradient(180deg,_rgba(0,50,41,0.2)_0%,_rgba(0,50,41,0.04)_100%),radial-gradient(146.13%_118.42%_at_50%_-15.5%,_rgba(255,255,255,0.128)_0%,_rgba(255,255,255,0)_100%)]"
              >
                {label}

                <button
                  className="ml-1 text-base font-medium hover:text-red-300"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemoveStat(value)
                  }}
                >
                  <CrossIcon className="text-black dark:text-white" />
                </button>
              </div>
            )
          })}
        </div>

        <div className="ml-2 shrink-0">
          <img src={Chevron} alt="Chevron Icon" className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {dropdownOpen && (
        <div className="absolute z-10 mt-1 w-full rounded shadow max-h-60 overflow-y-auto border text-sm bg-white text-black border-[rgba(0,0,0,0.1)] dark:bg-[#080816] dark:text-white dark:border-gray-600">
          <div className="px-4 py-2 text-md text-gray-700 dark:text-gray-200">{t("charts.nonCurrencyStats")}</div>
          {options
            .filter((opt) => !opt.type)
            .map(({ label, value }) => (
              <label key={value} className="flex items-center justify-between px-4 py-2 pl-8 hover:bg-gray-600 cursor-pointer text-sm">
                <span>{t(label)}</span>
                <input type="checkbox" checked={selectedStats.includes(value)} onChange={(e) => onToggleStat(value, e.target.checked)} />
              </label>
            ))}

          <div className="px-4 py-2 text-md border-t text-gray-700 border-gray-400 dark:text-gray-200 dark:border-gray-700">
            {t("charts.currencyStats")}
          </div>

          {options
            .filter((opt) => opt.type === "currency")
            .map(({ label, value }) => (
              <label key={value} className="flex items-center justify-between px-4 py-2 pl-8 hover:bg-gray-600 cursor-pointer text-sm">
                <span>{t(label)}</span>
                <input type="checkbox" checked={selectedStats.includes(value)} onChange={(e) => onToggleStat(value, e.target.checked)} />
              </label>
            ))}
        </div>
      )}
    </div>
  )
}
